import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  FormControlLabel,
  Switch,
  Button,
  Card,
  CardContent,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
// Same iconsax convention as every other icon in the app -- a small solid
// checkmark next to "Custom sound" so it's visually obvious at a glance that
// something's already been uploaded, not just readable from the text alone
// (Rahim, 2026-09-10: "there is no icon specifying the already added mp3").
import { TickCircle } from "iconsax-react";
import {
  fetchNotificationSettings,
  updateNotificationSettings,
  uploadNotificationSound,
  resetNotificationSound,
} from "../../../redux/Slice/Settings/notificationSlice";
import { toast } from "react-hot-toast";
import { checkBeforeOutbound } from "../../../utils/subscriptionError";
import { getAuthToken } from "../../../utils/getAuthToken";

const DEFAULT_SOUND_SRC = "/sounds/Notification.mp3";
const MAX_SOUND_SIZE_BYTES = 1 * 1024 * 1024; // keep in sync with server's MAX_SOUND_SIZE_BYTES

const Notifications = () => {
  const dispatch = useDispatch();
  const { settings, loading } = useSelector((state) => state.notifications);

  // Same Owner-bypass gap as Lifecycle's own gate (LifecycleStageChip.jsx) — the
  // stored Notifications.Edit boolean defaults to false even for Owner accounts
  // on roles saved before this permission existed.
  const roleName = useSelector((s) => s.userDetails?.userDetails?.roleName || null);
  const NotificationsPermissions = useSelector(
    (s) => s.userDetails?.userPermissions?.permissions?.Notifications || {}
  );
  const canEditSound = roleName === "Owner" || NotificationsPermissions.Edit === true;

  const [localSettings, setLocalSettings] = useState({
    notifyOnAllIncomingMessages: true,
    notifyOnMessagesFromAssignedContacts: false,
    notifyOnAssignment: true,
    ignoredChannels: [], // This will store workspace IDs
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [soundBusy, setSoundBusy] = useState(false); // uploading or resetting
  const soundFileInputRef = useRef(null);
  const soundPreviewRef = useRef(null);

  useEffect(() => {
    dispatch(fetchNotificationSettings());
  }, [dispatch]);

  useEffect(() => {
    if (settings) {
      setLocalSettings({
        notifyOnAllIncomingMessages: settings.notifyOnAllIncomingMessages ?? true,
        notifyOnMessagesFromAssignedContacts: settings.notifyOnMessagesFromAssignedContacts ?? false,
        notifyOnAssignment: settings.notifyOnAssignment ?? true,
        ignoredChannels: settings.channels
          ?.filter(ch => ch.isIgnored)
          ?.map(ch => ch.workspaceID || ch._id) || [],
      });
    }
  }, [settings]);

  const handleToggle = (setting) => {
    setLocalSettings((prev) => ({
      ...prev,
      [setting]: !prev[setting],
    }));
  };

  const handleChannelToggle = (workspaceID) => {
    setLocalSettings((prev) => {
      const isIgnored = prev.ignoredChannels.includes(workspaceID);
      return {
        ...prev,
        ignoredChannels: isIgnored
          ? prev.ignoredChannels.filter(id => id !== workspaceID)
          : [...prev.ignoredChannels, workspaceID],
      };
    });
  };

  const handleUpdate = async () => {
    const { blocked, message: blockMsg } = await checkBeforeOutbound(getAuthToken, []);
    if (blocked) { toast.error(blockMsg, { id: 'subscription-blocked', duration: 5000 }); return; }
    try {
      await dispatch(updateNotificationSettings({
        notifyOnAllIncomingMessages: localSettings.notifyOnAllIncomingMessages,
        notifyOnMessagesFromAssignedContacts: localSettings.notifyOnMessagesFromAssignedContacts,
        notifyOnAssignment: localSettings.notifyOnAssignment,
        ignoredChannels: localSettings.ignoredChannels,
      })).unwrap();
      
      setSnackbar({
        open: true,
        message: "Settings updated successfully!",
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error || "Failed to update settings",
        severity: "error",
      });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Account-wide notification sound -- shared by every team member, so only
  // Owner (or a role explicitly granted Notifications.Edit) can change it.
  const handleSoundFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file next time
    if (!file) return;

    if (file.type !== "audio/mpeg" && file.type !== "audio/mp3") {
      setSnackbar({ open: true, message: "Only MP3 files are supported for the notification sound.", severity: "error" });
      return;
    }
    if (file.size > MAX_SOUND_SIZE_BYTES) {
      setSnackbar({ open: true, message: `File is too large — please keep the notification sound under ${MAX_SOUND_SIZE_BYTES / (1024 * 1024)} MB.`, severity: "error" });
      return;
    }

    setSoundBusy(true);
    try {
      await dispatch(uploadNotificationSound(file)).unwrap();
      setSnackbar({ open: true, message: "Notification sound updated for the whole account.", severity: "success" });
    } catch (error) {
      setSnackbar({ open: true, message: error || "Failed to upload notification sound", severity: "error" });
    } finally {
      setSoundBusy(false);
    }
  };

  const handleResetSound = async () => {
    setSoundBusy(true);
    try {
      await dispatch(resetNotificationSound()).unwrap();
      setSnackbar({ open: true, message: "Reverted to the default notification sound.", severity: "success" });
    } catch (error) {
      setSnackbar({ open: true, message: error || "Failed to reset notification sound", severity: "error" });
    } finally {
      setSoundBusy(false);
    }
  };

  const handlePreviewSound = () => {
    const el = soundPreviewRef.current;
    if (!el) return;
    el.currentTime = 0;
    el.play().catch(() => {});
  };

  if (loading && !settings) {
  return (
      <Box
        sx={{
          px: { xs: 2, sm: 2.5, md: 2.5, lg: 3, xl: 3 },
          py: { xs: 2, sm: 2.5, md: 2.5, lg: 2.5, xl: 2.5 },
          minHeight: "min-content",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        borderRadius: "8px",
        backgroundColor: "background.paper",
        display: "flex",
        flexDirection: "column",
        color: "text.primary",
        /* Inset from parent so cards keep left/right breathing room on all breakpoints */
        px: { xs: 2, sm: 2.5, md: 2.5, lg: 3, xl: 3 },
        pt: { xs: 2, sm: 2.5, md: 2.5, lg: 2.5, xl: 2.5 },
        pb: { xs: 3, sm: 3, md: "60px", lg: "60px", xl: "60px" },
        gap: {
          xs: "15px",
          sm: "18px",
          md: "20px",
          lg: "20px",
          xl: "20px",
        },
        /* Let SettingsDetails (parent) scroll; avoid nested flex height that clips the 2nd card */
        minHeight: "min-content",
      }}
    >
        {/* Manage Notifications Card */}
        <Card
          sx={{
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
            backgroundColor: "background.paper",
            boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
            borderRadius: "8px",
            flexShrink: 0,
          }}
        >
        <CardContent>
          <Typography
            variant="h5"
            sx={{ marginBottom: "10px", fontWeight: "bold" }}
          >
            Manage Notifications
          </Typography>
          <Typography
            variant="body2"
            sx={{ marginBottom: "20px", color: "text.secondary" }}
          >
            Which notifications would you like to receive?
          </Typography>
          <Divider sx={{ marginBottom: "20px" }} />
          
          <Box sx={{ display: "flex", flexDirection: "column", gap: "15px" }}>
            <FormControlLabel
              control={
                <Switch
                  checked={localSettings.notifyOnAllIncomingMessages}
                  onChange={() => handleToggle("notifyOnAllIncomingMessages")}
                  color="primary"
                />
              }
              label="Notify on all incoming messages"
              sx={{ color: "text.primary" }}
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={localSettings.notifyOnMessagesFromAssignedContacts}
                  onChange={() => handleToggle("notifyOnMessagesFromAssignedContacts")}
                  color="primary"
                />
              }
              label="Notify on messages from contacts assigned to you"
              sx={{ color: "text.primary" }}
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={localSettings.notifyOnAssignment}
                  onChange={() => handleToggle("notifyOnAssignment")}
                  color="primary"
                />
              }
              label="Notify on assignment"
              sx={{ color: "text.primary" }}
            />
          </Box>
          
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpdate}
            disabled={loading}
            sx={{
              marginTop: "20px",
              paddingX: "2rem",
              "&:hover": {
                backgroundColor: "primary.dark",
              },
            }}
          >
            {loading ? "Updating..." : "Update Settings"}
          </Button>
        </CardContent>
      </Card>

      {/* Notification Sound Card — account-wide, shared by every team member.
          Only the account Owner (or a role explicitly granted Notifications.Edit
          in Roles & Permissions) can change it; everyone else sees it read-only. */}
      <Card
        sx={{
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
          backgroundColor: "background.paper",
          boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
          borderRadius: "8px",
          flexShrink: 0,
        }}
      >
        <CardContent>
          <Typography variant="h5" sx={{ marginBottom: "10px", fontWeight: "bold" }}>
            Notification Sound
          </Typography>
          <Typography variant="body2" sx={{ marginBottom: "20px", color: "text.secondary" }}>
            {canEditSound
              ? "This sound plays for every team member on this account when a new message arrives."
              : "This is the shared sound every team member on this account hears for new messages. Ask your account owner to change it."}
          </Typography>
          <Divider sx={{ marginBottom: "20px" }} />

          <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
              {settings?.soundUrl && (
                <Box sx={{ width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <TickCircle size="100%" color="#0E9046" variant="Bold" />
                </Box>
              )}
              <Typography sx={{ color: "text.primary", fontWeight: 500 }}>
                {settings?.soundUrl
                  ? settings?.soundFileName || "Custom sound"
                  : "Default sound"}
              </Typography>
            </Box>

            <Button variant="outlined" size="small" onClick={handlePreviewSound} disabled={soundBusy}>
              Play preview
            </Button>
            {/* Swaps src to whatever the account's current sound is -- default file or the uploaded one. */}
            <audio ref={soundPreviewRef} src={settings?.soundUrl || DEFAULT_SOUND_SRC} preload="none" />

            {canEditSound && (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={() => soundFileInputRef.current?.click()}
                  disabled={soundBusy}
                >
                  {soundBusy ? "Uploading..." : "Upload MP3"}
                </Button>
                <input
                  ref={soundFileInputRef}
                  type="file"
                  accept="audio/mpeg,.mp3"
                  hidden
                  onChange={handleSoundFileSelected}
                />
                {settings?.soundUrl && (
                  <Button variant="text" size="small" onClick={handleResetSound} disabled={soundBusy}>
                    Reset to default
                  </Button>
                )}
              </>
            )}
          </Box>
          <Typography sx={{ color: "text.secondary", fontSize: "0.75rem", marginTop: "10px" }}>
            MP3 only, under {MAX_SOUND_SIZE_BYTES / (1024 * 1024)} MB.
          </Typography>
        </CardContent>
      </Card>

      {/* Ignore Channels Card */}
      <Card
        sx={{
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
          backgroundColor: "background.paper",
          boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
          borderRadius: "8px",
        }}
      >
        <CardContent>
          <Typography
            variant="h5"
            sx={{ marginBottom: "10px", fontWeight: "bold" }}
          >
            Want to ignore some channels?
          </Typography>
          <Typography
            variant="body2"
            sx={{ marginBottom: "20px", color: "text.secondary" }}
          >
            Select the workspaces you want to ignore. You will not receive any notifications from these workspaces.
          </Typography>
            <Divider sx={{ marginBottom: "20px" }} />
            
            {settings?.channels && settings.channels.length > 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "15px",
                }}
              >
                {settings.channels.map((channel) => {
                  const workspaceID = channel.workspaceID || channel._id;
                  return (
                    <Box
                      key={workspaceID}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          color: "text.primary",
                          fontWeight: 500,
                          wordBreak: "break-word",
                          fontSize: { xs: "11px", sm: "14px" },
                        }}
                      >
                        {channel.name || "Unknown Workspace"}
                        {channel.isDefault && (
                          <Typography component="span" sx={{ color: "text.secondary", fontSize: "0.75rem", marginLeft: "8px" }}>
                            (Default)
                          </Typography>
                        )}
                      </Typography>
                      <Typography sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
                        {channel.phoneNumberID || 'No number'}
                      </Typography>
                    </Box>
                    <Switch
                      checked={localSettings.ignoredChannels.includes(workspaceID)}
                      onChange={() => handleChannelToggle(workspaceID)}
                      color="primary"
                    />
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Typography sx={{ color: "text.secondary" }}>
              No workspaces available
            </Typography>
          )}
          
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpdate}
            disabled={loading}
            sx={{
              marginTop: "20px",
              paddingX: "2rem",
              "&:hover": {
                backgroundColor: "primary.dark",
              },
            }}
          >
            {loading ? "Updating..." : "Update Settings"}
          </Button>
        </CardContent>
      </Card>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Notifications;
